import React from 'react';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import { Alert, Box, Button, InputAdornment, TextField, Typography } from '@mui/material';
import AppCustomIcon from '../../../../app/features/icon/AppIcon';
import styleVariables from '../../../../app/styles/styleVariables';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import useAuthOktaUserActivationResend from './hooks/useAuthOktaUserActivationResend';
import { Trans, useTranslation } from 'react-i18next';
import langAuthOktaUserActivationResend from './langs/langAuthOktaUserActivationResend';

interface IAuthOktaUserActivationResendForm {
	login: string;
	error: string;
}
const FAuthOktaUserActivationResend = () => {
	const sxTextField = {
		'& .MuiInputBase-root': {
			border: '1px solid #CCC',
		},
	};

	const { t } = useTranslation();

	const { mutate: userActivateResend, isSuccess, isLoading } = useAuthOktaUserActivationResend();

	const schema = yup
		.object({
			login: yup
				.string()
				.required(t(langAuthOktaUserActivationResend.field_error_email_required.tag))
				.email(t(langAuthOktaUserActivationResend.field_error_email_invalid.tag)),
		})
		.required();

	const {
		handleSubmit,
		control,
		setError,
		getValues,
		reset,
		formState: { errors },
	} = useForm<IAuthOktaUserActivationResendForm>({
		resolver: yupResolver(schema),
		// defaultValues: { login: localStorage.getItem('authLogin') ?? '' },
		defaultValues: { login: '' },
	});

	const onSubmit: SubmitHandler<IAuthOktaUserActivationResendForm> = (data) => {
		userActivateResend(data, {
			onSuccess: () => {
				reset();
			},
			onError: () => {
				setError('error', {
					type: 'custom',
					message: t(langAuthOktaUserActivationResend.text_error.tag),
				});
			},
		});
	};

	return (
		<>
			{isSuccess ? (
				<>
					<Alert severity="success" sx={{ marginTop: '30px' }}>
						<Trans
							t={t}
							i18nKey={langAuthOktaUserActivationResend.text_success.tag}
							values={{ login: getValues('login') }}
						/>
					</Alert>
				</>
			) : (
				<>
					{errors?.error ? (
						<>
							<Alert severity="error" sx={{ marginTop: '30px' }}>
								{' '}
								{errors?.error?.message}
							</Alert>
						</>
					) : (
						<form onSubmit={handleSubmit(onSubmit)}>
							<Typography variant="body2" gutterBottom sx={{ textAlign: 'left', marginBottom: '15px' }}>
								{t(langAuthOktaUserActivationResend.text_error_link_invalid.tag)}
							</Typography>
							<Controller
								name="login"
								control={control}
								//defaultValue={localStorage.getItem('authLogin') ?? ''}
								render={({ field }) => (
									<TextField
										variant="outlined"
										size="medium"
										sx={{ ...sxTextField, marginTop: '30px' }}
										label={t(langAuthOktaUserActivationResend.label_email.tag)}
										fullWidth
										autoComplete="off"
										type="text"
										{...field}
										error={!!errors.login}
										helperText={errors?.login?.message ?? ''}
										InputProps={{
											endAdornment: (
												<InputAdornment position="end">
													<AppCustomIcon
														tag="auth_login"
														sx={{ color: styleVariables.primaryColor }}
													/>
												</InputAdornment>
											),
										}}
									/>
								)}
							/>

							<Box style={{ textAlign: 'center', marginTop: '30px' }}>
								<Button
									type="submit"
									variant="contained"
									color="secondary"
									sx={{ minWidth: 160 }}
									endIcon={<AppCustomIcon tag="arrow-right" />}
									disabled={isLoading}
								>
									{t(langAuthOktaUserActivationResend.btn_resend_link.tag)}
								</Button>
							</Box>
						</form>
					)}
				</>
			)}
		</>
	);
};

export default FAuthOktaUserActivationResend;
